/**
 * Converts an HLS clip (a URL plus a time range) into an MP4 buffer using
 * Mediabunny's `Conversion` API. Mediabunny will transmux H.264/AAC HLS
 * segments directly to MP4 without transcoding when possible, so this is
 * fast.
 */

import {
	BufferTarget,
	Conversion,
	HLS_FORMATS,
	Input,
	Mp4OutputFormat,
	Output,
	UrlSource,
} from 'mediabunny';

export interface HlsToMp4Options {
	url: string;
	/** Inclusive start timestamp in the source's time base (seconds). */
	start: number;
	/** Exclusive end timestamp in the source's time base (seconds). */
	end: number;
	onProgress?: (progress: number, processedSeconds: number) => void;
	signal?: AbortSignal;
}

export class HlsToMp4ConversionError extends Error {
	constructor(message: string, public readonly cause?: unknown) {
		super(message);
		this.name = 'HlsToMp4ConversionError';
	}
}

export async function convertHlsToMp4(
	opts: HlsToMp4Options,
): Promise<ArrayBuffer> {
	if (opts.end <= opts.start) {
		throw new HlsToMp4ConversionError(
			`Invalid trim range: end (${opts.end}) must be greater than start (${opts.start}).`,
		);
	}

	const input = new Input({
		source: new UrlSource(opts.url),
		formats: HLS_FORMATS,
	});

	const output = new Output({
		format: new Mp4OutputFormat(),
		target: new BufferTarget(),
	});

	let conversion: Conversion | null = null;
	try {
		conversion = await Conversion.init({
			input,
			output,
			// Only keep the primary rendition pair to keep the MP4 small and
			// avoid fan-out for HLS ladders.
			tracks: 'primary',
			trim: {
				start: opts.start,
				end: opts.end,
			},
		});

		if (!conversion.isValid) {
			const reasons = conversion.discardedTracks
				.map((t) => `${t.track.type}: ${t.reason}`)
				.join('; ');
			throw new HlsToMp4ConversionError(
				`Conversion is invalid and cannot be executed (${reasons || 'no usable tracks'}).`,
			);
		}

		if (opts.onProgress) {
			conversion.onProgress = opts.onProgress;
		}

		if (opts.signal) {
			if (opts.signal.aborted) {
				throw new HlsToMp4ConversionError('Conversion aborted before start.');
			}
			opts.signal.addEventListener(
				'abort',
				() => {
					void conversion?.cancel();
				},
				{ once: true },
			);
		}

		await conversion.execute();

		const target = output.target as BufferTarget;
		if (!target.buffer) {
			throw new HlsToMp4ConversionError('Output buffer is empty.');
		}
		return target.buffer;
	} catch (err) {
		if (err instanceof HlsToMp4ConversionError) {
			throw err;
		}
		const message =
			err instanceof Error ? err.message : 'Unknown conversion error';
		throw new HlsToMp4ConversionError(message, err);
	} finally {
		try {
			await conversion?.cancel();
		} catch {
			// The conversion may have already completed or been canceled; that's fine.
		}
	}
}

/**
 * Triggers a browser download for the given MP4 buffer.
 */
export function downloadMp4Buffer(buffer: ArrayBuffer, filename: string): void {
	const blob = new Blob([buffer], { type: 'video/mp4' });
	const url = URL.createObjectURL(blob);
	const anchor = document.createElement('a');
	anchor.href = url;
	anchor.download = filename.endsWith('.mp4') ? filename : `${filename}.mp4`;
	document.body.appendChild(anchor);
	anchor.click();
	document.body.removeChild(anchor);
	// Allow the download to start before revoking the URL.
	setTimeout(() => URL.revokeObjectURL(url), 0);
}
